import { Injectable } from '@angular/core';
import {
  ClusterMarkerService,
  PinMarkerOptions,
  TileViewerReadyEvent,
  Plan as TileViewerPlan,
} from '@mydocma/tile-viewer';
import { Pin } from '@mydocma/tile-viewer/lib/pin/pin';
import { Point } from 'leaflet';
import { firstValueFrom, merge, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { PlanVersion } from '../bp/plans/plan-versions.interface';

export type CalibrationPinId = '1' | '2';

export interface CalibrationPinAnchor {
  x: number;
  y: number;
  z?: number;
}

export interface CalibrationPinPair {
  pin1: CalibrationPinAnchor;
  pin2: CalibrationPinAnchor;
}

export const CALIBRATION_DROP_OFFSET_Y = 10;

@Injectable({ providedIn: 'root' })
export class CalibrationPinService {

  public createTileViewerPlan(planVersion: PlanVersion): TileViewerPlan {
    return {
      id: 45,
      tileUrl: `${planVersion.tilesUrl}/{z}/{x}_{y}.jpg`,
      height: planVersion.renderHeight,
      width: planVersion.renderWidth,
      maxZoom: planVersion.maxLevel,
    };
  }

  public buildDroppedCalibrationPin(
    tileViewerData: TileViewerReadyEvent,
    dropPoint: { x: number; y: number },
    id: CalibrationPinId,
    markerOptions: PinMarkerOptions,
  ): Pin {
    const mapContainer = tileViewerData.mapService.map.getContainer();
    const containerRect = mapContainer.getBoundingClientRect();

    const point = new Point(
      dropPoint.x - containerRect.x,
      dropPoint.y - containerRect.y - CALIBRATION_DROP_OFFSET_Y,
    );

    const coords = tileViewerData.mapService.containerPointToPinCoords(point);

    return this.createCalibrationPin({ x: coords.x, y: coords.y }, id, markerOptions);
  }

  public createCalibrationPin(
    anchor: CalibrationPinAnchor,
    id: CalibrationPinId,
    markerOptions: PinMarkerOptions,
  ): Pin {
    return {
      id,
      anchor: { x: anchor.x, y: anchor.y },
      markerOptions,
    } as Pin;
  }

  public snapshotCalibrationPins(tileViewerData: TileViewerReadyEvent): [Pin, Pin] | null {
    const pins = tileViewerData.pinService.pins;

    if (!pins || pins.length !== 2) {
      return null;
    }

    const orderedPins = [...pins].sort((a, b) => Number(a.id) - Number(b.id));

    return [
      { ...orderedPins[0], anchor: { ...orderedPins[0].anchor } },
      { ...orderedPins[1], anchor: { ...orderedPins[1].anchor } },
    ];
  }

  public createCalibrationPinsSnapshotStream(
    tileViewerData: TileViewerReadyEvent,
    includeAddedPins: boolean,
  ): Observable<[Pin, Pin]> {
    const source$ = includeAddedPins
      ? merge(tileViewerData.pinService.onPinAdded, tileViewerData.pinEditService.onPinMoved$)
      : tileViewerData.pinEditService.onPinMoved$;

    return source$.pipe(
      filter(Boolean),
      map(() => this.snapshotCalibrationPins(tileViewerData)),
      filter((pins): pins is [Pin, Pin] => !!pins),
    );
  }

  public async restoreExistingPins(
    tileViewerData: TileViewerReadyEvent,
    points: CalibrationPinPair,
    markerOptions: PinMarkerOptions,
  ): Promise<void> {
    const pin1 = this.createCalibrationPin(points.pin1, '1', markerOptions);
    const pin2 = this.createCalibrationPin(points.pin2, '2', markerOptions);

    await this.addCalibrationPin(tileViewerData, pin1);
    await this.addCalibrationPin(tileViewerData, pin2);
  }

  public async addCalibrationPin(tileViewerData: TileViewerReadyEvent, pin: Pin): Promise<void> {
    (tileViewerData.markerService as ClusterMarkerService).clustering = false;
    tileViewerData.pinService.addPin(pin);
    await this.deselectAllPins(tileViewerData);
  }

  public async deselectAllPins(tileViewerData: TileViewerReadyEvent): Promise<void> {
    const allPins = await firstValueFrom(tileViewerData.pinService.pins$);
    tileViewerData.pinService.deselectPins(allPins);
  }
}
